import React, { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import DarkModeProvider, { useDarkMode } from './DarkModeProvider'
import ContrastModeProvider from './ContrastModeProvider'
import { CachedFlagsProvider } from './providers/CachedFlagsProvider'
import usePreloadPreferences from './data-service/usePreloadPreferences'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'

const EmptyLoading = (): JSX.Element => <div />

const devEnv = process.env.NODE_ENV === 'development'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = devEnv ? import('./dev.flags').then((m) => m.default) : undefined

const mockActivities = devEnv ? import('./dev.activities').then((m) => m.default) : undefined

/* Sentry initialization.
 * Sentry is disabled locally by default (SENTRY_DSN is unset and SENTRY_DISABLED=true in .env).
 * Snow runs locally via setupProxy.js and populates window.SERVER_DATA on every page load, so to enable Sentry in development, run:
 * SENTRY_DSN=<your-dsn> SENTRY_DISABLED=false npm start
 * Events will log under the "local" environment (TARGET_ENV in .env).
 * Additional debugging is gated conditionally by devEnv.
 */
if (sentryDSN) {
  /* Sample rates and strict filtering are hardcoded because CRA bakes REACT_APP_* env vars at BUILD time via webpack DefinePlugin — they cannot be changed at runtime via server config. To adjust these values, change them here and redeploy. */
  const sentryConfig = mergeSentryConfig({
    attachStacktrace: devEnv,
    debug: devEnv, // Log Sentry SDK diagnostics to the console in development.
    sampleRate: devEnv ? 1 : 0.01, // 0.0–1.0 fraction. 100% of events locally.
    strictFiltering: true, // Enhanced filtering via @fs/zion-config (https://github.com/fs-webdev/zion/pull/13771)
  })

  // eslint-disable-next-line no-console -- dev-locked debugging statement of value to the *browser* console.
  devEnv && console.log('Sentry config:', sentryConfig)

  Sentry.init(sentryConfig)
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch ones like dark mode.
const TopLevelAppWrapper = ({ children }): JSX.Element => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return children
}

const PageWrapper = (): JSX.Element => {
  const [isDarkMode] = useDarkMode()

  const hideHeaderFooter = window.location.pathname.includes('/print-view/')

  return (
    <Router basename={base} handledExternalRoutes={[{ path: '/tree/person' }]}>
      <Root
        // We position the `main` element inside the App component, since we have a subnav that we want to be outside of it. That same `main` element has an id of skipToMainContent so that the skip link works correctly.
        skipLinkId="skipToMainContent"
        as="div"
        theme={isDarkMode ? 'night' : 'day'}
        analytics
        header={{ hidden: hideHeaderFooter }}
        footer={{ hidden: hideHeaderFooter }}
      >
        <App />
      </Root>
    </Router>
  )
}

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  return (
    <TopLevelAppWrapper>
      <DarkModeProvider>
        {/* Single app-wide evaluation of shared feature flags; see provider docs. */}
        <CachedFlagsProvider>
          <ContrastModeProvider>
            <PageWrapper />
          </ContrastModeProvider>
        </CachedFlagsProvider>
      </DarkModeProvider>
    </TopLevelAppWrapper>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => (
  <StrictMode>
    <Suspense fallback={<EmptyLoading />}>
      <FrontierBaseProviders
        mockFlags={mockFlags}
        flagOptions={{ LoadingFallback: EmptyLoading }}
        mockActivities={mockActivities}
      >
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>

    {/* hide in production build */}
    {devEnv && <RebuggerClient />}
  </StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
